import { useCallback } from 'react';
import { v4 as uuidv4 } from 'uuid';
import { ENTITY_TYPES, REPORT_TYPES } from 'components/Wizards/Dashlet/types';
import { DEFAULT_LABEL_INDICATOR } from 'components/DashboardGrid/utils';
import { getDashletConstraints } from '../dimensions';

const pluginDefaults = getDashletConstraints({ area: 'plugin' });
import type { DashletLayout, DashletMutation } from './dashletTypes';

export const PLUGIN_CONTENT_CHART_TYPE = 'plugin' as const;

// Build the API config payload for a plugin block dashlet.
// Resize constraints (minW/maxW/minH/maxH) are always derived at runtime from
// the block definition via usePluginConstraintsMap — they are never stored.
export const buildPluginBlockConfig = (result: Record<string, any>) => ({
  entity_type: ENTITY_TYPES.PLUGIN,
  report_type: REPORT_TYPES.PLUGIN,
  chart_type: PLUGIN_CONTENT_CHART_TYPE,
  plugin_api_name: result.pluginApiName,
  block_api_name: result.blockApiName,
  fe_extra_info: {
    customStylesEnabled: true,
    dashletStyleConfig: { dropShadow: result.dropShadow },
  },
});

// Build the duplicate payload for a plugin block dashlet.
export const buildPluginBlockDuplicatePayload = (
  dashlet: Dashlet,
  name?: string
) => ({
  config: buildPluginBlockConfig({
    pluginApiName: dashlet.config?.pluginApiName,
    blockApiName: dashlet.config?.blockApiName,
    dropShadow: dashlet.config?.feExtraInfo?.dashletStyleConfig?.dropShadow,
  }),
  layout: {
    i: uuidv4(),
    w: dashlet.layout.w,
    h: dashlet.layout.h,
  },
  name: name || DEFAULT_LABEL_INDICATOR,
});

export const isPluginContentDashlet = (
  dashlet?: {
    config?: { chartType?: string };
  } | null
): boolean => dashlet?.config?.chartType === PLUGIN_CONTENT_CHART_TYPE;

type Dashlet = {
  id: string;
  name: string;
  layout: DashletLayout;
  config?: Record<string, any>;
};

type UsePluginContentBlockParams = {
  dashlets: Dashlet[];
  getDashletPosition: (dimensions: { w?: number; h?: number }) => {
    x: number;
    y: number;
  };
  createDashletMutation: DashletMutation;
  updateDashletMutation: DashletMutation;
  duplicateDashlet: (args: {
    payload: unknown;
    oldDashlet?: Dashlet;
    dashlets: Dashlet[];
    dashboardId: string;
  }) => Promise<unknown> | void;
};

type MutatePluginBlockArgs = {
  dashboardId: string;
  result?: any;
  dashletId?: string;
  isDuplicate?: boolean;
  name?: string;
};

export const usePluginContentBlock = ({
  dashlets,
  getDashletPosition,
  createDashletMutation,
  updateDashletMutation,
  duplicateDashlet,
}: UsePluginContentBlockParams) => {
  const mutatePluginBlock = useCallback(
    async ({
      dashboardId,
      result,
      dashletId,
      isDuplicate = false,
      name,
    }: MutatePluginBlockArgs) => {
      const existingDashlet = dashletId
        ? dashlets.find((d) => d.id === dashletId)
        : undefined;

      if (isDuplicate) {
        if (!existingDashlet) return;
        return duplicateDashlet({
          payload: buildPluginBlockDuplicatePayload(existingDashlet, name),
          oldDashlet: existingDashlet,
          dashlets,
          dashboardId,
        });
      }

      if (existingDashlet) {
        return updateDashletMutation.mutateAsync({
          dashletId,
          dashboardId,
          payload: {
            config: buildPluginBlockConfig(result),
            name: existingDashlet.name || DEFAULT_LABEL_INDICATOR,
            layout: {
              i: existingDashlet.layout.i,
              h: existingDashlet.layout.h,
              w: existingDashlet.layout.w,
              x: existingDashlet.layout.x,
              y: existingDashlet.layout.y,
            },
          },
        });
      }

      const dimensions = {
        w: result.minW ?? pluginDefaults.minW,
        h: result.minH ?? pluginDefaults.minH,
      };
      const position = getDashletPosition(dimensions);
      return createDashletMutation.mutateAsync({
        dashboardId,
        payload: {
          config: buildPluginBlockConfig(result),
          name: DEFAULT_LABEL_INDICATOR,
          layout: { i: uuidv4(), ...dimensions, ...position },
        },
      });
    },
    [
      dashlets,
      getDashletPosition,
      createDashletMutation,
      updateDashletMutation,
      duplicateDashlet,
    ]
  );

  return { mutatePluginBlock };
};
